import { useEffect, useState } from "react";

export default function AdminAnalytics() {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [debugInfo, setDebugInfo] = useState('');

  useEffect(() => {
    async function load() {
      setLoading(true);
      setError('');
      setDebugInfo('');
      try {
        const token = localStorage.getItem('token');
        const apiBase = (process.env.REACT_APP_API_BASE || 'http://localhost:4000').replace(/\/$/, '');
        const url = `${apiBase}/api/admin/analytics`;
        
        setDebugInfo(`Fetching: ${url}`);
        
        if (!token) {
          throw new Error('No auth token found. Please login as admin@sitescope.work first.');
        }

        if (process.env.NODE_ENV === 'development') {
          console.log('Fetching analytics from:', url);
        }

        const res = await fetch(url, {
          headers: { 
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        
        const json = await res.json();
        if (process.env.NODE_ENV === 'development') {
          console.log('Response status:', res.status);
          console.log('Response:', json);
        }
        
        if (!res.ok) {
          throw new Error(json.error || `HTTP ${res.status}: Failed to fetch analytics`);
        }
        
        setData(json.data);
      } catch (err) {
        console.error('Analytics error:', err);
        setError(err.message || 'Failed to load analytics');
      } finally {
        setLoading(false);
      }
    }
    load();
  }, []);

  if (loading) return <div className="p-6">Loading analytics…</div>;
  if (error) return (
    <div className="p-6">
      <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded">
        <h3 className="font-bold text-red-800 mb-2">Error</h3>
        <p className="text-red-600">{error}</p>
      </div>
      <div className="mb-4 p-4 bg-gray-50 border border-gray-200 rounded">
        <h4 className="font-semibold text-gray-700 mb-2">Debug Info</h4>
        <p className="text-sm text-gray-600 mb-2">{debugInfo}</p>
        <p className="text-sm text-gray-600">Backend URL: {(process.env.REACT_APP_API_BASE || 'http://localhost:4000')}</p>
        <p className="text-sm text-gray-600">Token present: {localStorage.getItem('token') ? 'Yes' : 'No'}</p>
      </div>
      <div className="p-4 bg-blue-50 border border-blue-200 rounded">
        <h4 className="font-semibold text-blue-800 mb-2">Troubleshooting</h4>
        <ul className="text-sm text-blue-700 space-y-1">
          <li>1. Make sure backend is running: <code>cd Ss-BackEnd && npm start</code></li>
          <li>2. Set ADMIN_EMAIL in backend .env: <code>ADMIN_EMAIL="admin@sitescope.work"</code></li>
          <li>3. Login as admin@sitescope.work in the frontend first</li>
          <li>4. Check backend console for errors</li>
        </ul>
      </div>
    </div>
  );

  const { totalUsers, byPlan, planDetails, recent30, recentSignups, todayCount, weekCount } = data || {};

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">User Analytics Dashboard</h1>
          <p className="text-gray-600">Real-time user registration and plan statistics (Local Only - Not Deployed)</p>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
            <div className="flex items-center justify-between mb-2">
              <div className="text-sm font-medium text-gray-500">Total Users</div>
              <div className="bg-blue-100 rounded-full p-2">
                <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                </svg>
              </div>
            </div>
            <div className="text-3xl font-bold text-gray-900">{totalUsers}</div>
          </div>

          <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
            <div className="flex items-center justify-between mb-2">
              <div className="text-sm font-medium text-gray-500">Today</div>
              <div className="bg-green-100 rounded-full p-2">
                <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v13m0-13V6a2 2 0 112 2h-2zm0 0V5.5A2.5 2.5 0 109.5 8H12zm-7 4h14M5 12a2 2 0 110-4h14a2 2 0 110 4M5 12v7a2 2 0 002 2h10a2 2 0 002-2v-7" />
                </svg>
              </div>
            </div>
            <div className="text-3xl font-bold text-gray-900">{todayCount || 0}</div>
            <div className="text-xs text-gray-500 mt-1">new signups</div>
          </div>

          <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
            <div className="flex items-center justify-between mb-2">
              <div className="text-sm font-medium text-gray-500">This Week</div>
              <div className="bg-purple-100 rounded-full p-2">
                <svg className="w-5 h-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
              </div>
            </div>
            <div className="text-3xl font-bold text-gray-900">{weekCount || 0}</div>
            <div className="text-xs text-gray-500 mt-1">last 7 days</div>
          </div>

          <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
            <div className="flex items-center justify-between mb-2">
              <div className="text-sm font-medium text-gray-500">Last 30 Days</div>
              <div className="bg-orange-100 rounded-full p-2">
                <svg className="w-5 h-5 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                </svg>
              </div>
            </div>
            <div className="text-3xl font-bold text-gray-900">{recent30}</div>
            <div className="text-xs text-gray-500 mt-1">new registrations</div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Plan Distribution */}
          <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Users by Plan</h2>
            {planDetails && planDetails.length > 0 ? (
              <div className="space-y-4">
                {planDetails.map(({ plan, count, recentUsers }) => {
                  const percentage = totalUsers > 0 ? ((count / totalUsers) * 100).toFixed(1) : 0;
                  const colors = {
                    free: 'bg-gray-200',
                    pro: 'bg-blue-500',
                    enterprise: 'bg-purple-500'
                  };
                  return (
                    <div key={plan}>
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-gray-900 capitalize">{plan}</span>
                          <span className="text-sm text-gray-500">({count} users)</span>
                        </div>
                        <span className="text-sm font-semibold text-gray-700">{percentage}%</span>
                      </div>
                      <div className="w-full bg-gray-100 rounded-full h-2.5 mb-1">
                        <div
                          className={`${colors[plan] || 'bg-gray-500'} h-2.5 rounded-full transition-all`}
                          style={{ width: `${percentage}%` }}
                        ></div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : byPlan ? (
              <div className="space-y-2">
                {Object.entries(byPlan).map(([plan, count]) => (
                  <div key={plan} className="flex justify-between text-sm p-2 bg-gray-50 rounded">
                    <span className="capitalize font-medium">{plan}</span>
                    <span className="font-semibold">{count}</span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-500">No plan data available</p>
            )}
          </div>

          {/* Recent Signups */}
          <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Recent Signups (Last 10)</h2>
            {recentSignups && recentSignups.length > 0 ? (
              <div className="space-y-3 max-h-96 overflow-y-auto">
                {recentSignups.map((signup, idx) => (
                  <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">{signup.email}</p>
                      <p className="text-xs text-gray-500">
                        {new Date(signup.signupDate).toLocaleString('en-US', {
                          month: 'short',
                          day: 'numeric',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </p>
                    </div>
                    <span className={`ml-3 px-2.5 py-1 rounded-full text-xs font-medium capitalize ${
                      signup.plan === 'pro' ? 'bg-blue-100 text-blue-800' :
                      signup.plan === 'enterprise' ? 'bg-purple-100 text-purple-800' :
                      'bg-gray-100 text-gray-800'
                    }`}>
                      {signup.plan}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-500">No recent signups</p>
            )}
          </div>
        </div>

        {/* Setup Instructions */}
        <div className="mt-8 bg-blue-50 border border-blue-200 rounded-lg p-6">
          <h3 className="text-sm font-semibold text-blue-900 mb-2">📝 Local Setup Instructions</h3>
          <p className="text-sm text-blue-800 mb-3">
            This analytics page is for local/administrative use only and is NOT deployed to production.
          </p>
          <div className="bg-white rounded p-3 text-xs font-mono text-gray-800 overflow-auto">
            <p className="mb-2"># Add to Ss-BackEnd/.env:</p>
            <p className="mb-2">ADMIN_EMAIL="admin@sitescope.work"</p>
            <p className="mb-2"># After starting the backend and logging in as the admin user, open:</p>
            <p className="font-medium">http://localhost:3000/admin-analytics</p>
          </div>
        </div>
      </div>
    </div>
  );
}
